/* One controller survives AJAX replacements without cloning product forms. */
(() => {
  if (window.__beleaevPairingsMotionInitialized) return;
  window.__beleaevPairingsMotionInitialized = true;

  const DISMISS_KEY = 'beleaev:cart-pairings-dismissed:v1';
  let dismissed = document.documentElement.dataset.beleaevCartPairingsDismissed === 'true';

  function dismissPairings() {
    dismissed = true;
    document.documentElement.dataset.beleaevCartPairingsDismissed = 'true';
    try {
      localStorage.setItem(DISMISS_KEY, 'true');
    } catch (error) {
      // Restricted storage still keeps recommendations dismissed on this page.
    }
  }

  const SPEED = 22;
  const IDLE_HOLD = 1800;
  const tracks = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  let frame = 0;
  let wakeTimer = 0;
  let lastTime = 0;
  let discoveryQueued = false;

  const baseReady = state => state.track.isConnected && state.visible && state.max > 1 &&
    !state.panel.closest('[hidden], [aria-hidden="true"]') &&
    (!state.drawer || state.drawer.classList.contains('active'));
  const interacting = state => state.hover || state.pointer || state.panel.contains(document.activeElement);
  const stateFor = target => {
    const panel = target.closest?.('.bel-cart-pairings');
    return panel ? tracks.get(panel.querySelector('.bel-cart-pairings-track')) : undefined;
  };

  function start() {
    if (!frame && !document.hidden && !reducedMotion.matches) {
      clearTimeout(wakeTimer);
      wakeTimer = 0;
      frame = requestAnimationFrame(tick);
    }
  }

  function hold(state) {
    if (!state) return;
    state.holdUntil = performance.now() + IDLE_HOLD;
    state.velocity = 0;
    state.position = state.track.scrollLeft;
    start();
  }

  function tick(time) {
    frame = 0;
    if (document.hidden || reducedMotion.matches) {
      lastTime = 0;
      return;
    }
    const delta = Math.min((time - (lastTime || time)) / 1000, .064);
    lastTime = time;
    let moving = false;
    let wait = Infinity;
    tracks.forEach(state => {
      if (!baseReady(state) || interacting(state)) {
        state.velocity = 0;
        return;
      }
      if (time < state.holdUntil) {
        wait = Math.min(wait, state.holdUntil - time);
        return;
      }
      if (!state.velocity) state.position = state.track.scrollLeft;
      const distance = state.direction > 0 ? state.max - state.position : state.position;
      if (distance < .75) {
        state.direction *= -1;
        state.velocity = 0;
      }
      const targetSpeed = SPEED * Math.max(.15, Math.min(1, distance / 32));
      state.velocity += (targetSpeed - state.velocity) * Math.min(1, delta * 6);
      state.position = Math.max(0, Math.min(state.max, state.position + state.direction * state.velocity * delta));
      state.track.scrollLeft = state.position;
      state.written = state.track.scrollLeft;
      moving = true;
    });
    if (moving) frame = requestAnimationFrame(tick);
    else {
      lastTime = 0;
      if (Number.isFinite(wait)) wakeTimer = setTimeout(start, wait + 20);
    }
  }

  const visibility = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = tracks.get(entry.target);
      if (state) state.visible = entry.isIntersecting && entry.intersectionRatio > 0;
    });
    start();
  });
  const size = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const state = tracks.get(entry.target);
      if (state) state.max = Math.max(0, state.track.scrollWidth - state.track.clientWidth);
    });
    start();
  });

  function discover() {
    discoveryQueued = false;
    if (dismissed) {
      document.querySelectorAll('#CartDrawer .beleaev-cart-pinned-upsell, #CartDrawer .bel-cart-pairings:not([data-empty-cart-recommendations])').forEach(panel => panel.remove());
    }
    tracks.forEach((state, track) => {
      if (track.isConnected) return;
      visibility.unobserve(track);
      size.unobserve(track);
      tracks.delete(track);
    });
    document.querySelectorAll('.bel-cart-pairings-track').forEach(track => {
      if (tracks.has(track)) {
        tracks.get(track).max = Math.max(0, track.scrollWidth - track.clientWidth);
        return;
      }
      const panel = track.closest('.bel-cart-pairings');
      if (!panel) return;
      tracks.set(track, {
        track, panel, drawer: track.closest('cart-drawer'),
        direction: 1, velocity: 0, position: track.scrollLeft, written: track.scrollLeft,
        max: Math.max(0, track.scrollWidth - track.clientWidth),
        visible: false, hover: matchMedia('(hover: hover)').matches && panel.matches(':hover'),
        pointer: false, holdUntil: performance.now() + 800
      });
      visibility.observe(track);
      size.observe(track);
    });
    start();
  }

  const mutations = new MutationObserver(records => {
    const relevant = records.some(record => record.type === 'childList' ||
      record.target.matches?.('cart-drawer, .bel-cart-pairings, .bel-cart-pairings-track'));
    if (!relevant || discoveryQueued) return;
    discoveryQueued = true;
    queueMicrotask(discover);
  });
  mutations.observe(document.documentElement, {
    childList: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'hidden', 'aria-hidden']
  });

  document.addEventListener('pointerover', event => {
    if (event.pointerType !== 'mouse') return;
    const state = stateFor(event.target);
    if (!state || state.panel.contains(event.relatedTarget)) return;
    state.hover = true;
    hold(state);
  });
  document.addEventListener('pointerout', event => {
    if (event.pointerType !== 'mouse') return;
    const state = stateFor(event.target);
    if (!state || state.panel.contains(event.relatedTarget)) return;
    state.hover = false;
    hold(state);
  });
  document.addEventListener('pointerdown', event => {
    const state = stateFor(event.target);
    if (state) state.pointer = true;
    hold(state);
  }, { passive: true });
  ['pointerup', 'pointercancel'].forEach(type => document.addEventListener(type, () => {
    tracks.forEach(state => {
      if (!state.pointer) return;
      state.pointer = false;
      hold(state);
    });
  }, { passive: true }));
  ['focusin', 'focusout', 'wheel'].forEach(type => document.addEventListener(type, event => {
    hold(stateFor(event.target));
  }, { passive: true }));
  document.addEventListener('scroll', event => {
    const state = tracks.get(event.target);
    if (!state || Math.abs(state.track.scrollLeft - state.written) < 1) return;
    state.written = state.track.scrollLeft;
    hold(state);
  }, { capture: true, passive: true });
  document.addEventListener('visibilitychange', start);
  reducedMotion.addEventListener('change', start);

  document.addEventListener('click', event => {
    const closeButton = event.target.closest?.('[data-pairings-close]');
    if (closeButton) {
      const panel = closeButton.closest('.bel-cart-pairings');
      if (!panel) return;
      const drawer = closeButton.closest('#CartDrawer');
      dismissPairings();
      (panel.closest('.beleaev-cart-pinned-upsell') || panel).remove();
      drawer?.querySelector('.drawer__close')?.focus();
      discover();
      return;
    }
    const button = event.target.closest?.('[data-pairing-direction]');
    if (!button) return;
    const track = button.closest('.bel-cart-pairings-carousel')?.querySelector('.bel-cart-pairings-track');
    const card = track?.querySelector('.bel-cart-pairing');
    const direction = Number(button.dataset.pairingDirection);
    if (!track || !card || (direction !== 1 && direction !== -1)) return;
    hold(tracks.get(track));
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    track.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
  });
  discover();
})();

